"use client";

import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { StepProps, BaseFormData } from "../components/types";
import { ABNAutocomplete } from "../components/ABNAutocomplete";
import { ABNEntity, formatABN } from "@/lib/abn-lookup";
import { ValidationRules } from "../components/FormValidation";
import Link from "next/link";
import { useState, useEffect } from "react";
import { Input } from "@/components/ui/input";
// import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import parse from "html-react-parser";
import { useFormContext } from "react-hook-form";

type AbnLookupStepProps<T = BaseFormData> = StepProps<T>; // Generic but constrained to BaseFormData

export const AbnLookupStep = <T extends BaseFormData = BaseFormData>({ formData, title, description, manualEntryTitle, manualEntryLabel, manualEntryHelperText, manualEntryRequired, manualEntryABNRequired }: AbnLookupStepProps<T>) => {
  const { setValue } = useFormContext();
  const [selectedEntity, setSelectedEntity] = useState<ABNEntity | undefined>();
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{
    abn?: string;
    postcode?: string;
    businessName?: string;
  }>({});

  // Standardized validation functions using ValidationRules
  const validateABN = (abn: string): string | undefined => {
    // If manual entry is required and no value provided, show error
    if (manualEntryRequired && !abn) {
      return `${manualEntryLabel || 'ABN'} is required`;
    }
    
    // If no value provided and not required, it's valid
    if (!abn) return undefined;
    
    // Clean the value (remove spaces and non-digits)
    const cleanedValue = abn.replace(/\D/g, '');
    
    // When manualEntryRequired is true, accept both ABN (11 digits) and NZBN (13 digits)
    if (manualEntryRequired) {
      if (!/^\d{11}$|^\d{13}$/.test(cleanedValue)) {
        return `${manualEntryLabel || 'ABN'} must be exactly 11 digits (ABN) or 13 digits (NZBN)`;
      }
    } else {
      // By default, only accept ABN (11 digits)
      if (!/^\d{11}$/.test(cleanedValue)) {
        return 'ABN must be exactly 11 digits';
      }
    }
    
    return undefined;
  };

  const validatePostcode = (postcode: string): string | undefined => {
    const rules = ValidationRules.postcode("Postcode");
    if (!postcode && rules.required) return rules.required as string;
    if (postcode && rules.pattern && !rules.pattern.value.test(postcode)) {
      return rules.pattern.message as string;
    }
    return undefined;
  };

  const validateBusinessName = (businessName: string): string | undefined => {
    const rules = ValidationRules.businessName("Business name");
    if (!businessName?.trim() && rules.required) return rules.required as string;
    if (businessName?.trim() && rules.minLength && businessName.trim().length < rules.minLength.value) {
      return rules.minLength.message as string;
    }
    return undefined;
  };

  const clearValidationErrors = () => {
    setValidationErrors({});
  };

  // Restore selectedEntity state when component mounts or formData changes
  useEffect(() => {
    if (formData.dataSource === 'autocomplete' && formData.selectedABN && formData.businessSearch) {
      const reconstructedEntity: ABNEntity = {
        abn: formData.selectedABN as string,
        entityStatus: 'Active',
        entityType: formData.entityType as string || 'Private Company',
        gstStatus: formData.gstStatus as string,
        businessName: formData.businessName ? [formData.businessName as string] : undefined,
        tradingName: formData.businessName ? [formData.businessName as string] : undefined,
        legalName: formData.businessName as string,
        mainBusinessLocation: {
          state: formData.state as string || '',
          postcode: formData.postcode as string || '',
        },
      };
      setSelectedEntity(reconstructedEntity);
      setIsManualEntry(false);
      if (formData.businessSearch && typeof formData.businessSearch === 'string' && formData.businessSearch.includes('-business-')) {
        const businessName = formData.businessName as string || 'Unknown Business';
        setValue('businessSearch' as never, businessName as unknown as never, { shouldDirty: true });
      }
    } else if (formData.dataSource === 'manual' && formData.selectedABN && formData.businessName) {
      setIsManualEntry(true);
      setSelectedEntity(undefined);
    }
  }, [formData.dataSource, formData.selectedABN, formData.businessName, formData.state, formData.postcode, formData.entityType, formData.gstStatus, formData.businessSearch, setValue]);
  
  const handleManualEntry = () => {
    setIsManualEntry(true);
    setSelectedEntity(undefined);
    clearValidationErrors();
    setValue('businessSearch' as never, '' as unknown as never, { shouldDirty: true });
    setValue('selectedABN' as never, '' as unknown as never, { shouldDirty: true });
    setValue('businessName' as never, '' as unknown as never, { shouldDirty: true });
    setValue('state' as never, '' as unknown as never, { shouldDirty: true });
    setValue('postcode' as never, '' as unknown as never, { shouldDirty: true });
    setValue('entityType' as never, '' as unknown as never, { shouldDirty: true });
    setValue('gstStatus' as never, '' as unknown as never, { shouldDirty: true });
    setValue('countryOfRegistration' as never, 'Australia' as unknown as never, { shouldDirty: true });
    setValue('businessType' as never, 'Private Company' as unknown as never, { shouldDirty: true });
    setValue('dataSource' as never, 'manual' as unknown as never, { shouldDirty: true });
  };

  const handleBackToSearch = () => {
    setIsManualEntry(false);
    clearValidationErrors();
    setValue('selectedABN' as never, '' as unknown as never, { shouldDirty: true });
    setValue('businessName' as never, '' as unknown as never, { shouldDirty: true });
    setValue('state' as never, '' as unknown as never, { shouldDirty: true });
    setValue('postcode' as never, '' as unknown as never, { shouldDirty: true });
    setValue('entityType' as never, '' as unknown as never, { shouldDirty: true });
    setValue('gstStatus' as never, '' as unknown as never, { shouldDirty: true });
    setValue('countryOfRegistration' as never, '' as unknown as never, { shouldDirty: true });
    setValue('dataSource' as never, undefined as unknown as never, { shouldDirty: true });
  };

  const handleBusinessSelect = (value: string, entity?: ABNEntity) => {
    setValue('businessSearch' as never, value as unknown as never, { shouldDirty: true });
    setSelectedEntity(entity);
    if (entity) {
      const businessName = entity.businessName?.[0] || entity.tradingName?.[0] || entity.legalName || '';
      setValue('selectedABN' as never, entity.abn as unknown as never, { shouldDirty: true });
      setValue('businessName' as never, businessName as unknown as never, { shouldDirty: true });
      setValue('state' as never, (entity.mainBusinessLocation?.state || '') as unknown as never, { shouldDirty: true });
      setValue('postcode' as never, (entity.mainBusinessLocation?.postcode || '') as unknown as never, { shouldDirty: true });
      setValue('entityType' as never, (entity.entityType || '') as unknown as never, { shouldDirty: true });
      setValue('gstStatus' as never, (entity.gstStatus || '') as unknown as never, { shouldDirty: true });
      setValue('countryOfRegistration' as never, 'Australia' as unknown as never, { shouldDirty: true });
      setValue('dataSource' as never, 'autocomplete' as unknown as never, { shouldDirty: true });
    }
  };

  const handleManualEntryChange = (field: string, value: string) => {
    // Handle ABN formatting with mask
    if (field === 'selectedABN') {
      // Remove all non-digits
      const digitsOnly = value.replace(/\D/g, '');
      
      // Limit to 11 or 13 digits based on manualEntryRequired
      const maxDigits = manualEntryRequired ? 13 : 11;
      const limitedDigits = digitsOnly.slice(0, maxDigits);
      
      // Format with mask (2-3-3-3 for ABN, 2-3-3-3-2 for NZBN) while typing
      let formattedValue = '';
      if (limitedDigits.length > 0) {
        formattedValue += limitedDigits.slice(0, 2);
        if (limitedDigits.length > 2) {
          formattedValue += ' ' + limitedDigits.slice(2, 5);
        }
        if (limitedDigits.length > 5) {
          formattedValue += ' ' + limitedDigits.slice(5, 8);
        }
        if (limitedDigits.length > 8) {
          formattedValue += ' ' + limitedDigits.slice(8, 11);
        }
        // For NZBN (13 digits), add the last 2 digits
        if (limitedDigits.length > 11 && manualEntryRequired) {
          formattedValue += ' ' + limitedDigits.slice(11, 13);
        }
      }
      
      setValue(field as never, formattedValue as unknown as never, { shouldDirty: true, shouldTouch: true });
      
      if (limitedDigits.length > 0) {
        const abnError = validateABN(limitedDigits);
        setValidationErrors(prev => ({ ...prev, abn: abnError }));
      } else {
        setValidationErrors(prev => ({ ...prev, abn: undefined }));
      }
    } else {
      setValue(field as never, value as unknown as never, { shouldDirty: true, shouldTouch: true });
    }
    if (field === 'postcode') {
      if (value.length > 0) {
        const postcodeError = validatePostcode(value);
        setValidationErrors(prev => ({ ...prev, postcode: postcodeError }));
      } else {
        setValidationErrors(prev => ({ ...prev, postcode: undefined }));
      }
    }
    if (field === 'businessName') {
      if (value.length > 0) {
        const businessNameError = validateBusinessName(value);
        setValidationErrors(prev => ({ ...prev, businessName: businessNameError }));
      } else {
        setValidationErrors(prev => ({ ...prev, businessName: undefined }));
      }
    }
  };

  const handleFieldBlur = (field: string, value: string) => {
    if (field === 'selectedABN') {
      // Extract digits only for validation
      const digitsOnly = value.replace(/\D/g, '');
      const abnError = validateABN(digitsOnly);
      setValidationErrors(prev => ({ ...prev, abn: abnError }));
    }
    if (field === 'postcode') {
      const postcodeError = validatePostcode(value);
      setValidationErrors(prev => ({ ...prev, postcode: postcodeError }));
    }
    if (field === 'businessName') {
      const businessNameError = validateBusinessName(value);
      setValidationErrors(prev => ({ ...prev, businessName: businessNameError }));
    }
  };

  // handleNextStep removed - now handled by centralized submit button

  return (
    <div className="flex flex-col gap-12">
      {/* Main Title */}
      <div className="flex flex-col gap-4">
        <h3>{title}</h3>
        {description && (
          <p className="paragraph-lg text-muted-foreground [&_strong]:text-foreground">{parse(description)}</p>
        )}
      </div>

      {/* Search Section */}
      <div className="flex flex-col gap-2"> 
        <Label htmlFor="businessSearch" className="paragraph-base font-bold mb-2">Search for an ABN, ACN, or Business Name</Label>
        <ABNAutocomplete
          value={formData.businessSearch as string || ""}
          onValueChange={handleBusinessSelect}
          placeholder=""
          disabled={isManualEntry}
        />
        <p className="paragraph-sm text-muted-foreground">
          Data provided by the <Link href="https://abr.business.gov.au/" target="_blank" rel="noopener noreferrer">Australian Business Register</Link> (ABR).
        </p>
        {selectedEntity && !isManualEntry && (
          <>
            <div className="mt-12 p-4 bg-muted/50 flex md:hidden items-center gap-4 border border-border rounded-lg">
              <div className="paragraph-base text-foreground flex flex-col flex-1 gap-1">
                <div className="font-bold">{selectedEntity.businessName?.[0] || selectedEntity.legalName}</div>
                <div>{formatABN(selectedEntity.abn || "")}</div>
                {selectedEntity.mainBusinessLocation?.state && (
                  <div className="flex flex-wrap gap-1">
                    {selectedEntity.mainBusinessLocation.postcode && `${selectedEntity.mainBusinessLocation.postcode} `}
                    {selectedEntity.mainBusinessLocation.state}
                  </div>
                )}
              </div>
              <Button type="button" variant="outline" grayBorder size="iconLg" onClick={() => handleBusinessSelect("", undefined)} className="" title="Clear selection">
                <span className="material-symbols-outlined">delete</span>
              </Button>
            </div>
            <div className="mt-12 hidden md:flex">
              <div className="paragraph-sm flex flex-col flex-1">
                <div className="flex h-12 items-center border-b border-border">
                  <div className="text-muted-foreground px-4 flex-1">Name</div>
                  <div className="text-muted-foreground px-4 w-[160px]">ABN</div>
                  {selectedEntity.mainBusinessLocation?.state && (
                    <div className="text-muted-foreground px-4 w-[160px]">Location</div>
                  )}
                </div>
                <div className="flex items-center border-b border-border py-1">
                  <div className="px-4 flex-1 py-3">{selectedEntity.businessName?.[0] || selectedEntity.legalName}</div>
                  <div className="px-4 w-[160px]">{formatABN(selectedEntity.abn || "")}</div>
                  <div className="px-4 w-[160px] flex gap-2 justify-between items-center">
                    {selectedEntity.mainBusinessLocation?.state && `${selectedEntity.mainBusinessLocation.postcode} ${selectedEntity.mainBusinessLocation.state}`}
                    <Button type="button" variant="outline" grayBorder size="iconSm" onClick={() => handleBusinessSelect("", undefined)} className="" title="Clear selection">
                      <span className="material-symbols-outlined">delete</span>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Manual Entry Form */}
      {isManualEntry && (
        <div className="flex flex-col gap-6 bg-muted rounded-lg py-4 px-4 pb-14 md:px-6">
          <div className="flex items-center justify-between">
            <span className="paragraph-sm text-foreground">{(manualEntryTitle as string) || 'Prefer searching for your business?'}</span>
            <Button variant="link" linkWithoutArrow onClick={handleBackToSearch} className="flex items-center text-foreground">
              <span className="paragraph-sm font-bold">Back to Search</span>
              <span className="material-symbols-outlined font-bold">expand_less</span>
            </Button>
          </div>

          {/* Registered Business Name */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="legalName" className="paragraph-base font-bold">Registered Business Name</Label>
            <Input id="legalName" value={formData.businessName as string || ""} maxLength={500} onChange={(e) => handleManualEntryChange('businessName', e.target.value)} onBlur={(e) => handleFieldBlur('businessName', e.target.value)} placeholder="" className={`w-full ${validationErrors.businessName ? 'border-red-500 focus:border-red-500' : ''}`} />
            {validationErrors.businessName ? (
              <p className="paragraph-sm text-red-500">{validationErrors.businessName}</p>
            ) : (
              <p className="paragraph-sm text-muted-foreground">This may differ from your trading name.</p>
            )}
          </div>

          {/* ABN */}
          <div className="flex flex-col gap-2">
            <Label htmlFor="selectedABN" className="paragraph-base font-bold">{manualEntryLabel as string || 'ABN'} <span className="font-normal text-muted-foreground">{manualEntryRequired || manualEntryABNRequired ? '' : '(Optional)'}</span></Label>
            <Input id="selectedABN" value={formData.selectedABN as string || ""} onChange={(e) => handleManualEntryChange('selectedABN', e.target.value)} onBlur={(e) => handleFieldBlur('selectedABN', e.target.value)} placeholder="" className={`w-full ${validationErrors.abn ? 'border-red-500 focus:border-red-500' : ''}`} />
            {validationErrors.abn ? (
              <p className="paragraph-sm text-red-500">{validationErrors.abn}</p>
            ) : (
              manualEntryHelperText ? (
                <p className="paragraph-sm text-muted-foreground">{parse(manualEntryHelperText as string)}</p>
              ) : (
                <p className="paragraph-sm text-muted-foreground">You can find it on <Link href="https://abr.business.gov.au/" target="_blank" rel="noopener noreferrer" className="text-red-500 underline">ABN Lookup</Link> if you&apos;re unsure.</p>
              )
            )}
          </div>

          {/* Postcode and State */}
          {/* <div className="grid md:grid-cols-2 gap-6 md:gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="postcode" className="paragraph-base font-bold">Postcode</Label>
              <Input id="postcode" value={formData.postcode as string || ""} onChange={(e) => handleManualEntryChange('postcode', e.target.value)} onBlur={(e) => handleFieldBlur('postcode', e.target.value)} placeholder="" className={`w-full ${validationErrors.postcode ? 'border-red-500 focus:border-red-500' : ''}`} />
              {validationErrors.postcode && (<p className="paragraph-sm text-red-500">{validationErrors.postcode}</p>)}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="state" className="paragraph-base font-bold">State</Label>
              <Select value={formData.state as string || ""} onValueChange={(value) => handleManualEntryChange('state', value)}>
                <SelectTrigger className="w-full"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="NSW">New South Wales</SelectItem>
                  <SelectItem value="VIC">Victoria</SelectItem>
                  <SelectItem value="QLD">Queensland</SelectItem>
                  <SelectItem value="WA">Western Australia</SelectItem>
                  <SelectItem value="SA">South Australia</SelectItem>
                  <SelectItem value="TAS">Tasmania</SelectItem>
                  <SelectItem value="ACT">Australian Capital Territory</SelectItem>
                  <SelectItem value="NT">Northern Territory</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div> */}
        </div>
      )}

      {/* Manual Entry Option */}
      {!isManualEntry && (
        <div className="bg-gray-100 rounded-xl py-4 px-6">
          <div className="flex items-center justify-between gap-3">
            <span className="paragraph-sm text-foreground">{(manualEntryTitle as string) || 'Cannot find your business in the list?'}</span>
            <Button variant="link" linkWithoutArrow onClick={handleManualEntry} className="flex items-center text-foreground">
              <span className="paragraph-sm font-bold">Enter manually</span>
              <span className="material-symbols-outlined font-bold">expand_more</span>
            </Button>
          </div>
        </div>
      )}

      {/* Button removed - now handled by centralized submit button */}
    </div>
  );
}; 